import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

export interface SentryUserInfo {
  userId?: string | number;
  companyId?: string | number;
}

/**
 * Module-level state shared between the Angular DI instance and the
 * `getSentryServiceInstance` factory. The factory is used in `beforeSend`,
 * which runs outside Angular's DI; both paths must read the same object.
 */
let sharedUserInfo: SentryUserInfo = {};

/**
 * Angular wrapper around the Sentry SDK providing user context management,
 * manual event capture, and event title transformation with structured prefixes.
 *
 * Module-level `sharedUserInfo` is intentionally shared with the `beforeSend`
 * hook (which runs outside Angular's DI) via the `getSentryServiceInstance` factory.
 * All Sentry event prefixes follow the format:
 *   `<TYPE>: <userId> | <companyId>: <original message>`
 */
@Injectable({
  providedIn: 'root'
})
export class SentryService {

  /**
   * Stores userId and companyId so they appear as a prefix on every outgoing
   * Sentry event. Also writes `user_id` and `company_id` as searchable tags.
   * @param info - Partial user context to merge into the current session state.
   */
  setUserInfo(info: SentryUserInfo): void {
    sharedUserInfo = { ...sharedUserInfo, ...info };
    Sentry.setTag('user_id', info.userId?.toString());
    Sentry.setTag('company_id', info.companyId?.toString());
  }

  /**
   * Clears the stored user context and removes the Sentry user identity.
   * Call on logout to stop attributing future events to the previous session.
   */
  clearUserInfo(): void {
    sharedUserInfo = {};
    Sentry.setUser(null);
  }

  /** Returns the currently stored user context. */
  getUserInfo(): SentryUserInfo {
    return sharedUserInfo;
  }

  /**
   * Sends a custom message to Sentry tagged with `custom: true` so it is
   * prefixed with `CUSTOM:` by `transformEventTitle` in `beforeSend`.
   * @param message - The message string to capture.
   * @param level   - Sentry severity level (default: `'info'`).
   * @param extra   - Optional key-value pairs attached as extra context.
   */
  captureMessage(message: string, level: Sentry.SeverityLevel = 'info', extra?: Record<string, unknown>): void {
    Sentry.captureMessage(message, {
      level,
      tags: { custom: true },
      extra,
    });
  }

  /**
   * Captures an exception and forwards it to Sentry with optional extra context.
   * @param error - The Error instance to capture.
   * @param extra - Optional key-value pairs attached as extra context.
   */
  captureException(error: Error, extra?: Record<string, unknown>): void {
    Sentry.captureException(error, { extra });
  }

  /**
   * Captures a Firebase permission-denied error with enriched context so
   * occurrences can be filtered and triaged in Sentry by auth state.
   * @param error           - The FirebaseError with code/message fields.
   * @param isAuthenticated - Whether the user had a valid session at error time.
   */
  captureFirebasePermissionError(error: { code?: string; message?: string }, isAuthenticated: boolean): void {
    const exception = error instanceof Error
      ? error
      : new Error(error?.message || 'Firebase: Missing or insufficient permissions');
    Sentry.withScope(scope => {
      scope.setTag('firebase.permission_error', 'true');
      scope.setExtras({ firebase_error_code: error?.code, is_authenticated: isAuthenticated });
      Sentry.captureException(exception);
    });
  }

  /**
   * Applies a structured prefix to a Sentry event and classifies it as
   * CUSTOM, ERROR, WARNING, or LOG based on origin and level.
   * LOG-level events are dropped by returning null, which causes `beforeSend`
   * to discard them entirely — matching iq-webapp behaviour.
   *
   * @param event - The Sentry event passed by reference from `beforeSend`.
   * @returns The mutated event with the prefix applied, or null to drop it.
   */
  transformEventTitle(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
    const { userId, companyId } = sharedUserInfo;
    const originalTitle = event.exception?.values?.[0]?.value || event.message || 'Unknown Error';
    const userPrefix = this.buildUserPrefix(userId, companyId);

    let messageType: 'CUSTOM' | 'LOG' | 'ERROR' | 'WARNING';
    if (event.tags?.['custom'] === true) {
      messageType = 'CUSTOM';
    } else if (event.logger === 'console') {
      switch (event.level) {
        case 'error':   messageType = 'ERROR';   break;
        case 'warning': messageType = 'WARNING'; break;
        default:        messageType = 'LOG';
      }
    } else if (event.level === 'warning') {
      messageType = 'WARNING';
    } else {
      messageType = 'ERROR';
    }

    if (messageType === 'LOG') {
      return null;
    }

    // Sentry passes the event by reference in beforeSend; mutating it directly is intentional.
    event.message = `${messageType}: ${userPrefix}: ${originalTitle}`;

    if (event.exception?.values?.[0]) {
      event.exception.values[0].value = event.message;
    }

    return event;
  }

  /**
   * Builds the `<userId> | <companyId>` prefix string used in event titles.
   * Falls back to whichever ID is available, or `'Unknown'` if neither is set.
   */
  private buildUserPrefix(userId?: string | number, companyId?: string | number): string {
    if (userId && companyId) return `${userId} | ${companyId}`;
    return String(userId ?? companyId ?? 'Unknown');
  }
}

let sentryServiceInstance: SentryService | null = null;

/**
 * Returns the module-level singleton `SentryService` instance.
 * Used exclusively by `beforeSend`, which runs outside Angular's DI context.
 */
export const getSentryServiceInstance = (): SentryService => {
  if (!sentryServiceInstance) {
    sentryServiceInstance = new SentryService();
  }
  return sentryServiceInstance;
};

/**
 * Convenience wrapper around `getSentryServiceInstance().transformEventTitle`.
 * Wire this directly into `Sentry.init({ beforeSend })`.
 * @param event - The Sentry event from `beforeSend`.
 * @returns The transformed event, or null to drop it.
 */
export const transformEventTitle = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  return getSentryServiceInstance().transformEventTitle(event);
};
